import React, { useState, useRef } from 'react';
import './PatternGenerator.css';

interface Element {
  id: string;
  type: 'circle' | 'square' | 'triangle' | 'star';
  x: number;
  y: number;
}

interface PatternSection {
  elements: Element[];
}

const PatternGenerator: React.FC = () => {
  const [selectedElement, setSelectedElement] = useState<'circle' | 'square' | 'triangle' | 'star'>('circle');
  const [sections, setSections] = useState<PatternSection[]>([
    { elements: [] },
    { elements: [] },
    { elements: [] },
    { elements: [] }
  ]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const GRID_SIZE = 20;
  const SECTION_WIDTH = 300;
  const SECTION_HEIGHT = 300;

  const addElement = (sectionIndex: number, x: number, y: number) => {
    const newElement: Element = {
      id: `${Date.now()}-${Math.random()}`,
      type: selectedElement,
      x,
      y
    };

    setSections(prev => prev.map((section, index) => 
      index === sectionIndex 
        ? { ...section, elements: [...section.elements, newElement] }
        : section
    ));
  };

  const removeElement = (sectionIndex: number, elementId: string) => {
    setSections(prev => prev.map((section, index) => 
      index === sectionIndex 
        ? { ...section, elements: section.elements.filter(el => el.id !== elementId) }
        : section
    ));
  };

  const handleGridClick = (sectionIndex: number, event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = Math.floor((event.clientX - rect.left) / GRID_SIZE) * GRID_SIZE + GRID_SIZE / 2;
    const clickY = Math.floor((event.clientY - rect.top) / GRID_SIZE) * GRID_SIZE + GRID_SIZE / 2;
    
    // Check if there's already an element at this position
    const existingElement = sections[sectionIndex].elements.find(el => 
      Math.abs(el.x - clickX) < GRID_SIZE / 2 && Math.abs(el.y - clickY) < GRID_SIZE / 2
    );
    
    if (existingElement) {
      // Remove the existing element
      removeElement(sectionIndex, existingElement.id);
    } else {
      // Add a new element
      addElement(sectionIndex, clickX, clickY);
    }
  };

  const copySection = (fromIndex: number, toIndex: number) => {
    setSections(prev => prev.map((section, index) => 
      index === toIndex 
        ? { elements: [...prev[fromIndex].elements.map(el => ({ ...el, id: `${Date.now()}-${Math.random()}` }))] }
        : section
    ));
  };

  const clearSection = (sectionIndex: number) => {
    setSections(prev => prev.map((section, index) => 
      index === sectionIndex 
        ? { elements: [] }
        : section
    ));
  };

  const renderElement = (element: Element) => {
    const size = 16;
    const style = {
      position: 'absolute' as const,
      left: element.x - size / 2,
      top: element.y - size / 2,
      width: size,
      height: size,
      pointerEvents: 'none' as const
    };

    switch (element.type) {
      case 'circle':
        return (
          <div
            key={element.id}
            style={{
              ...style,
              backgroundColor: '#000',
              borderRadius: '50%'
            }}
          />
        );
      case 'square':
        return (
          <div
            key={element.id}
            style={{
              ...style,
              backgroundColor: '#000'
            }}
          />
        );
      case 'triangle':
        return (
          <div
            key={element.id}
            style={{
              ...style,
              width: 0,
              height: 0,
              borderLeft: `${size/2}px solid transparent`,
              borderRight: `${size/2}px solid transparent`,
              borderBottom: `${size}px solid #000`,
              left: element.x - size / 2,
              top: element.y - size / 2
            }}
          />
        );
      case 'star':
        return (
          <div
            key={element.id}
            style={{
              ...style,
              fontSize: `${size}px`,
              lineHeight: '1',
              color: '#000',
              fontFamily: 'Arial, sans-serif'
            }}
          >
            ★
          </div>
        );
    }
  };

  const exportToPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Scale factor to add spacing between elements
    const SPACING_SCALE = 2.2; // Spread elements 2.2x further apart for 20px elements
    
    // Calculate bounds for each cluster (sections with elements)
    const clusterBounds = sections.map(section => {
      if (section.elements.length === 0) return null;
      
      // Get the center of the pattern
      const xs = section.elements.map(el => el.x);
      const ys = section.elements.map(el => el.y);
      const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
      const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
      
      // Scale positions from center to add spacing
      const scaledXs = xs.map(x => centerX + (x - centerX) * SPACING_SCALE);
      const scaledYs = ys.map(y => centerY + (y - centerY) * SPACING_SCALE);
      
      return {
        minX: Math.min(...scaledXs) - 25, // Increased padding for 40px elements
        maxX: Math.max(...scaledXs) + 25,
        minY: Math.min(...scaledYs) - 25,
        maxY: Math.max(...scaledYs) + 25,
        width: Math.max(...scaledXs) - Math.min(...scaledXs) + 50,
        height: Math.max(...scaledYs) - Math.min(...scaledYs) + 50,
        centerX,
        centerY,
        scaledXs,
        scaledYs
      };
    });

    // Calculate cluster spacing - consistent gap between clusters
    const CLUSTER_GAP = 60;
    const MARGIN = 40;
    
    // Calculate total width needed
    let totalWidth = MARGIN * 2;
    const clusterWidths: number[] = [];
    
    clusterBounds.forEach((bounds, index) => {
      if (bounds) {
        clusterWidths[index] = bounds.width;
        totalWidth += bounds.width;
      } else if (index === 3) {
        // Fourth section is empty - space for question mark
        clusterWidths[index] = 100; // Question mark width (increased for 120px font)
        totalWidth += 100;
      } else {
        clusterWidths[index] = 0;
      }
      
      if (index < 3) totalWidth += CLUSTER_GAP;
    });

    // Find a common baseline Y coordinate from the original (unscaled) positions
    let baselineY = 150; // Middle of the 300px grid (SECTION_HEIGHT / 2)
    
    // Find the actual baseline from existing elements (use center Y of first non-empty cluster)
    for (let i = 0; i < sections.length; i++) {
      if (sections[i].elements.length > 0) {
        const ys = sections[i].elements.map(el => el.y);
        baselineY = (Math.min(...ys) + Math.max(...ys)) / 2;
        break; // Use the first cluster's center as baseline
      }
    }
    
    // Find the actual top and bottom bounds of all scaled elements
    let minScaledY = Infinity;
    let maxScaledY = -Infinity;
    
    sections.forEach((section, sectionIndex) => {
      const bounds = clusterBounds[sectionIndex];
      if (bounds && section.elements.length > 0) {
        section.elements.forEach(element => {
          const offsetFromBaseline = element.y - baselineY;
          const scaledOffset = offsetFromBaseline * SPACING_SCALE;
          const actualY = scaledOffset; // Y position relative to baseline
          
          minScaledY = Math.min(minScaledY, actualY - 20); // -20 for element radius
          maxScaledY = Math.max(maxScaledY, actualY + 20); // +20 for element radius
        });
      }
    });
    
    // If no elements, use default bounds
    if (minScaledY === Infinity) {
      minScaledY = -50;
      maxScaledY = 50;
    }
    
    const contentHeight = maxScaledY - minScaledY;
    const totalHeight = contentHeight + MARGIN * 2;

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    // Draw clusters with consistent Y-axis alignment
    let currentX = MARGIN;
    
    sections.forEach((section, sectionIndex) => {
      const bounds = clusterBounds[sectionIndex];
      
      if (bounds && section.elements.length > 0) {
        // Draw elements for this cluster using scaled positions
        section.elements.forEach((element, elementIndex) => {
          // Use scaled positions for proper spacing
          const scaledX = bounds.centerX + (element.x - bounds.centerX) * SPACING_SCALE;
          
          const x = currentX + (scaledX - bounds.minX);
          
          // Align all elements to the same baseline Y coordinate, centered in canvas
          const offsetFromBaseline = element.y - baselineY;
          const scaledOffset = offsetFromBaseline * SPACING_SCALE;
          const y = MARGIN + (scaledOffset - minScaledY); // Position relative to top of content area
          
          const size = 40; // Increased to 40px (20px radius for circles, 40x40 for squares)

          switch (element.type) {
            case 'circle':
              ctx.fillStyle = '#7bb3f0'; // Pastel blue color
              ctx.beginPath();
              ctx.arc(x, y, size / 2, 0, 2 * Math.PI);
              ctx.fill();
              break;
            case 'square':
              ctx.fillStyle = '#7bb3f0'; // Pastel blue color
              ctx.fillRect(x - size / 2, y - size / 2, size, size);
              break;
            case 'triangle':
              ctx.fillStyle = '#7bb3f0'; // Pastel blue color
              ctx.beginPath();
              ctx.moveTo(x, y - size / 2);
              ctx.lineTo(x - size / 2, y + size / 2);
              ctx.lineTo(x + size / 2, y + size / 2);
              ctx.closePath();
              ctx.fill();
              break;
            case 'star':
              ctx.fillStyle = '#7bb3f0'; // Pastel blue color
              ctx.font = `${size}px Arial`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText('★', x, y);
              break;
          }
        });
        
        currentX += clusterWidths[sectionIndex];
      } else if (sectionIndex === 3 && section.elements.length === 0) {
        // Fourth section is empty - draw question mark
        const questionMarkX = currentX + clusterWidths[sectionIndex] / 2;
        // Center question mark vertically in the same area as the content
        const questionMarkY = MARGIN + contentHeight / 2; // True center of content area
        
        ctx.fillStyle = '#666';
        ctx.font = 'bold 120px Arial'; // Increased to 120px for better proportion with 40px elements
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('?', questionMarkX, questionMarkY);
        
        currentX += clusterWidths[sectionIndex];
      } else {
        currentX += clusterWidths[sectionIndex];
      }
      
      // Add gap between clusters
      if (sectionIndex < 3) {
        currentX += CLUSTER_GAP;
      }
    });

    // Export
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pattern-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="pattern-generator">
      <h2>Pattern Generator</h2>
      
      {/* Element Selector */}
      <div className="element-selector">
        <span>Elements: </span>
        {(['circle', 'square', 'triangle', 'star'] as const).map(type => (
          <button
            key={type}
            className={`element-btn ${selectedElement === type ? 'active' : ''}`}
            onClick={() => setSelectedElement(type)}
          >
            {type === 'circle' && '●'}
            {type === 'square' && '■'}
            {type === 'triangle' && '▲'}
            {type === 'star' && '★'}
          </button>
        ))}
      </div>

      {/* Pattern Sections */}
      <div className="pattern-sections">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="section-container">
            <div
              className="pattern-section"
              onClick={(e) => handleGridClick(sectionIndex, e)}
            >
              {section.elements.map(renderElement)}
            </div>
            
            <div className="section-controls">
              <button onClick={() => clearSection(sectionIndex)}>Clear</button>
              {sectionIndex < 3 && (
                <button onClick={() => copySection(sectionIndex, sectionIndex + 1)}>
                  Copy →
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Export Button */}
      <div className="export-controls">
        <button className="export-btn" onClick={exportToPNG}>
          Export as PNG
        </button>
      </div>

      {/* Hidden canvas for export */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default PatternGenerator;